import React, { useEffect, useState } from "react";
import { Link, useNavigate, } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Swal from "sweetalert2";
import useApi from "../../../core/customhook/useApi";
import dayjs from "dayjs";

interface EmployeeResignation {
    release_date: string,
    employee_name: string;
    employee_id: string;
    band: string;  // assuming band is a string, could be a number depending on the data format
    work_location: string;
    status: string;  // assuming status is a string, if it's a number, change the type to number
    department: string;  // assuming department is a string, if it's a number, change the type to number
    designation: string;  // assuming designation is a string, if it's a number, change the type to number
    email_id: string;
    join_date: string;  // Date format as string, can also be a Date object if needed
    date_of_birth: string;  // Date format as string, can also be a Date object if needed
    resignation_date: string;  // Date format as string, can also be a Date object if needed
}

const initialEmployeeResignationState: EmployeeResignation = {
    employee_name: "",
    employee_id: "",
    band: "",
    work_location: "",
    status: "",
    department: "",
    designation: "",
    email_id: "",
    join_date: "",
    date_of_birth: "",
    release_date: "",
    resignation_date: "",
};

type RatingState = {
    [index: number]: number;
};

type RatingExitFormProps = {
    data: RatingData;
    isSubmitted: boolean;
    onChange: (update: Partial<RatingData>) => void;
};


type RatingData = {
    ratings: RatingState;
    changesRecommended: string;
    rejoinReason: string;
};


type ExitInterviewFormData = {
    personalDetails: {
        personalEmail: string
    };
    rating: RatingData;
    feedback: {
        reasons: string[];
    };
};


type FeedBackQuestionProps = {
    reasons: string[];
    isSubmitted: boolean;
    setReasons: React.Dispatch<React.SetStateAction<string[]>>;
};


const particulars = [
    "Organisational Culture",
    "Learning Development",
    "Career Advancement & Growth",
    "Recognition",
    "Material & Equipment to deliver",
    "Freedom to implement ideas",
    "Performance Review",
    "Companies Policies",
    "Job Role",
    "Compensation",
    "Support received from manager and superior",
]

const options = [
    "Better Compensation",
    "Better Organisation",
    "Better Job Role",
    "Medical Illness",
    "Relocation",
    "Higher Education",
    "Promotion to the next level",
    "Personal Reason",
    "Others(mention reason)",
    "Poor Work Culture",
    "Poor Assignment",
    "Strained  Manager Employee Relationship",
    "Poor HR Policies",
    "Management style",
    "Hours of work",
    "Transfer",
    "Retirement",
    "Others(mention reason)",
];

const ExitInterviewPage = () => {
    const [activeTab, setActiveTab] = useState("personalRating");
    const initialRatings: RatingState = {};
    particulars.forEach((_, index) => {
        initialRatings[index] = 0;
    });
    const [formData, setFormData] = useState<ExitInterviewFormData>({
        personalDetails: {
            personalEmail: ""
        },

        rating: {
            ratings: initialRatings,
            changesRecommended: "",
            rejoinReason: "",
        },

        feedback: {
            reasons: [],
        },
    })

    const [isSubmitted, setIsSubmitted] = useState(false);
    const navigate = useNavigate()

    const tabs = [
        { key: "personalRating", label: "Personal Information" },
        { key: "ratingform", label: "Rating" },
        { key: "advance", label: "Feedback Question" },
    ];

    // Find the index of the active tab
    const activeTabIndex = tabs.findIndex(tab => tab.key === activeTab);

    // Go to the previous tab
    const handleBack = () => {
        if (activeTabIndex > 0) {
            setActiveTab(tabs[activeTabIndex - 1].key);
        }
    };

    // Go to the next tab
    const handleNext = () => {
        if (activeTabIndex < tabs.length - 1) {
            setActiveTab(tabs[activeTabIndex + 1].key);
        }
    };

    // get Api
    const { callApi: getExitInterviewApi, loading: getLoading } = useApi(
        "/api/fetch-feedback",
        {
            method: "GET",
            autoCall: false,
        }
    );
    // Personal details Api
    const { callApi: PersonaldetailApi, } = useApi('/api/offboarding-addmail', {
        method: "POST",
        autoCall: false,
    })

    // Rating Api
    const { callApi: ratingApi, } = useApi('/api/resignation-feedback', {
        method: "POST",
        autoCall: false,
    })

    // Feedback Api
    const { callApi: feedbackApi, } = useApi('/api/feedbackquestion', {
        method: "POST",
        autoCall: false,
    })


    useEffect(() => {
        const fetchExitInterview = async () => {
            try {
                const res = await getExitInterviewApi();

                if (!res?.data) return;

                const { rating, feedback, personaldetails } = res.data;

                if (rating?.ratings?.length > 0 || feedback?.reasons?.length > 0) {
                    setIsSubmitted(true);
                }


                // Convert ratings array → RatingState
                const ratingState: RatingState = {};
                rating.ratings.forEach((item: any, index: number) => {
                    ratingState[index] = item.rating;
                });

                setFormData(prev => ({
                    ...prev,
                    personalDetails: {
                        personalEmail: personaldetails.personalemail
                    },
                    rating: {
                        ratings: ratingState,
                        changesRecommended: rating.changes_recommended ?? "",
                        rejoinReason: rating.rejoin_reason ?? "",
                    },
                    feedback: {
                        reasons: feedback?.reasons ?? [],
                    },
                }));

            } catch (error) {
                Swal.fire("Error", "Failed to load exit interview data", "error");
            }
        };

        fetchExitInterview();
    }, []);



    const handleSubmit = async () => {

        const email = formData?.personalDetails?.personalEmail?.trim();

        if (!email) {
            Swal.fire("Warning", "Please enter personal email", "warning");
            return;
        }


        if (formData.feedback.reasons.length === 0) {
            Swal.fire("Warning", "Please select at least one reason", "warning")
            return
        }

        // Confirmation Alert 
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to submit the exit interview?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, submit",
            cancelButtonText: "Cancel"
        });

        // If user cancels
        if (!result.isConfirmed) return;

        // STEP 2: Build Rating Payload

        const ratingsPayload = particulars.map((item, index) => ({
            particular: item,
            rating: formData.rating?.ratings[index] ?? 0
        }));


        const ratingPayload = {
            changes_recommended: formData.rating.changesRecommended,
            rejoin_reason: formData.rating.rejoinReason,
            ratings: ratingsPayload,
        }

        const feedbackPayload = {
            reasons: formData.feedback.reasons,
        }

        const personalDetailsPayload = {
            personalEmail: formData.personalDetails.personalEmail
        }

        try {
            // STEP 3: Call APIs (Sequential)
            await Promise.all([
                await ratingApi(ratingPayload),
                await feedbackApi(feedbackPayload),
                await PersonaldetailApi(personalDetailsPayload)
            ])


            Swal.fire("Success", "Exit interview submitted successfully", "success")
            // setActiveTab("personalRating")
            navigate("/resignation/clearance")
        } catch (error) {
            Swal.fire("Error", "Submission failed. Please try again.", "error")
        }
    }

    console.log("Personal Email:", formData.personalDetails.personalEmail);

    const renderForm = () => {
        switch (activeTab) {
            case "personalRating":
                return <PersonalDetails
                    personalEmail={formData.personalDetails.personalEmail}
                    onEmailChange={(email: string) => {

                        setFormData(prev => ({
                            ...prev,
                            personalDetails: {
                                ...prev.personalDetails,
                                personalEmail: email,
                            },
                        }));
                    }}
                    isSubmitted={isSubmitted} />;
            case "ratingform":
                return (
                    <RatingExitForm
                        data={formData.rating}
                        isSubmitted={isSubmitted}
                        onChange={(updated) => {
                            setFormData(prev => ({
                                ...prev,
                                rating: {
                                    ...prev.rating,
                                    ...updated,
                                },
                            }));
                        }}
                    />
                );
            case "advance":
                return <FeedBackQuestion
                    reasons={formData.feedback.reasons}
                    isSubmitted={isSubmitted}
                    setReasons={(update) => {
                        setFormData((prev) => ({
                            ...prev,
                            feedback: {
                                reasons:
                                    typeof update === "function"
                                        ? update(prev.feedback.reasons)
                                        : update,
                            },
                        }));
                    }}
                />
            default:
                return null;
        }
    };


    // const handlePersonalEmailChange = 


    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Exit Interview</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.employeeDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item inactive">
                                        Offboarding
                                    </li>
                                    <li className="breadcrumb-item active">
                                        Exit Interview
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="head-icons ms-2">
                            <CollapseHeader />
                        </div>
                    </div>

                    {/* Card */}
                    <div className="card">
                        <div className="card-header">
                            <h5>Exit Interview Form</h5>
                        </div>

                        <div className="card-body">
                            {/* Tabs */}
                            <div className="d-flex flex-nowrap gap-2 mb-4 overflow-auto">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        className="btn px-3"
                                        style={{
                                            backgroundColor:
                                                activeTab === tab.key ? "#2e3294" : "white",
                                            color:
                                                activeTab === tab.key ? "#ffffff" : "#2e3294",
                                            borderRadius: "15px",
                                            border: "1px solid #2e3294",
                                        }}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Form Render */}
                            {renderForm()}
                            <div className="d-flex justify-content-between mt-4">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleBack}
                                    disabled={activeTabIndex === 0}
                                >
                                    Back
                                </button>

                                {activeTab === "advance" ? (
                                    !isSubmitted && (
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleSubmit}
                                        >
                                            Submit
                                        </button>
                                    )
                                ) : (
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleNext}
                                        disabled={activeTabIndex === tabs.length - 1}
                                    >
                                        Next
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            < div className="footer text-center border-top bg-white p-3" >
                © {new Date().getFullYear()} WeConnect.All rights reserved.
            </div >
        </>
    );
};

export default ExitInterviewPage;


// Rating Exit Form

const RatingExitForm = ({ data, isSubmitted, onChange }: RatingExitFormProps) => {

    const handleChange = (rowIndex: number, value: number) => {
        onChange({
            ratings: {
                ...data.ratings,
                [rowIndex]: value,
            },
        });
    };

    const handleChangeRecommended = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        onChange({ changesRecommended: e.target.value });
    };

    const handleRejoinReason = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        onChange({ rejoinReason: e.target.value });
    };

    return (
        <table className="table table-bordered text-center">
            <thead>
                <tr>
                    <th rowSpan={2} className="bg-primary text-white">
                        Particulars
                    </th>
                    <th colSpan={5} className="bg-primary text-white">
                        Rating (1 = Lowest, 5 = Highest)
                    </th>
                </tr>
                <tr>
                    {[1, 2, 3, 4, 5].map(num => (
                        <th key={num}>{num}</th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {particulars.map((item, rowIndex) => (
                    <tr key={rowIndex}>
                        <td className="text-start">{item}</td>
                        {[1, 2, 3, 4, 5].map(num => (
                            <td key={num}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name={`rating-${rowIndex}`}
                                    checked={data.ratings?.[rowIndex] === num}
                                    onChange={() => handleChange(rowIndex, num)}
                                    disabled={isSubmitted}
                                />
                            </td>
                        ))}
                    </tr>
                ))}

                <tr>
                    <td colSpan={6}>
                        <label className="form-control mb-2 text-start bg-primary text-white" >Any Changes Recommended in above particulars:-.</label>
                        <input
                            className="form-control"
                            required
                            // placeholder="Any changes recommended"
                            readOnly={isSubmitted}
                            value={data.changesRecommended}
                            // onChange={e => handleRatingFieldChange("changesRecommended", e.target.value)}
                            onChange={handleChangeRecommended}
                        />
                    </td>
                </tr>

                <tr>
                    <td colSpan={6}>
                        <label className="form-control mb-2 text-start bg-primary text-white" >If given an opportunity, would you like to join back? Please share reasons.</label>
                        <input
                            className="form-control"
                            // placeholder="Would you like to rejoin? Why?"
                            value={data.rejoinReason}
                            readOnly={isSubmitted}
                            // onChange={e => handleRatingFieldChange("rejoinReason", e.target.value)}
                            onChange={handleRejoinReason}
                            required
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    )
}


// Employee Exit Form

const PersonalDetails = ({ personalEmail, onEmailChange, isSubmitted, }: any) => {

    const [data, setData] = useState({
        initialEmployeeResignationState
    })
    const { data: employeeDetails,
        loading: employeeLoad,
        error
    } = useApi("/api/resignation-user-peronsalmail", {
        method: "GET",
        autoCall: true
    })

    const Employee: EmployeeResignation = employeeDetails?.data
    // const resignationDate = Employee?.resignation_date ? dayjs(Employee?.resignation_date) : null;
    // const joinDate = Employee?.join_date ? dayjs(Employee?.join_date) : null;


    // console.log("API RESPONSE :", Employee)

    return (
        <>


            {/* Page Heading */}

            {/* Card */}
            <div className="">
                <div className="card-body">
                    <div className="">
                        <div className="card">
                            <div className="card-body">

                                <div className="container">
                                    <div className="row mb-3 d-flex align-items-center justify-content-between ">
                                        <label className="col-3 col-form-label">
                                            Employee Name
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.employee_name ?? ""} readOnly />
                                        </div>
                                        <label className="col-3 col-form-label">
                                            Employee band
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.band ?? ""} readOnly />
                                        </div>
                                    </div>
                                    <div className="row mb-3 d-flex align-items-center justify-content-between ">
                                        <label className="col-3 col-form-label">
                                            Employee Id
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.employee_id ?? ""} readOnly />
                                        </div>
                                        <label className="col-3 col-form-label">
                                            Location
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.work_location ?? ""} readOnly />
                                        </div>
                                    </div>
                                    <div className="row mb-3 d-flex align-items-center justify-content-between ">
                                        <label className="col-3 col-form-label">
                                            Department
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.department ?? ""} readOnly />
                                        </div>
                                        <label className="col-3 col-form-label">
                                            Release Date
                                        </label>
                                        <div className="col-3">
                                            <input className="form-control datetimepicker" disabled value={dayjs(Employee?.release_date).format("DD MMMM YYYY") ?? ""} />
                                        </div>
                                    </div>
                                    <div className="row mb-3 d-flex align-items-center justify-content-between ">
                                        <label className="col-3 col-form-label">
                                            Designation
                                        </label>
                                        <div className="col-3">
                                            <input type="text" className="form-control" value={Employee?.designation ?? ""} readOnly />
                                        </div>
                                        <label className="col-3 col-form-label">
                                            Personal Email-Id
                                        </label>
                                        <div className="col-3">
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={personalEmail}
                                                disabled={isSubmitted}
                                                onChange={(e) => onEmailChange(e.target.value)}
                                                placeholder="Enter your personal email"
                                                required={isSubmitted}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-3 d-flex align-items-center justify-content-start">
                                        <label className="col-3 col-form-label">
                                            Date of Joining
                                        </label>
                                        <div className="col-3">
                                            <input className="form-control datetimepicker"
                                                value={dayjs(Employee?.join_date).format("DD MMMM YYYY") ?? ""}
                                                readOnly />
                                        </div>
                                        <label className="col-3 col-form-label">
                                            Date of Resignation
                                        </label>
                                        <div className="col-3">
                                            <input className="form-control datetimepicker"
                                                value={dayjs(Employee?.resignation_date).format("DD MMMM YYYY") ?? ""}
                                                placeholder="DD-MM-YYYY"
                                                name="to_date"
                                                readOnly />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    )
}



// Feeedback form

const FeedBackQuestion = ({ reasons, isSubmitted, setReasons }: FeedBackQuestionProps) => {
    const handleChange = (option: string) => {
        setReasons((prev) =>
            prev.includes(option)
                ? prev.filter((item) => item !== option)
                : [...prev, option]
        );
    };

    const safeReasons = Array.isArray(reasons) ? reasons : []

    return (
        <>
            {/* Card */}
            <div className="">
                <div className="card-body d-flex flex-column">
                    <span className="text-dark">
                        What is your Primary reason for leaving the organization? Tick one or all of them.
                    </span>
                    <hr className="border-dark" />

                    {/* <div className="container">
                        <div className="row">
                            {options.map((option, index) => (
                                <div key={index} className="col-6 mb-3 d-flex align-items-center justify-content-between form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={safeReasons.includes(option)}
                                        onChange={() => handleChange(option)}
                                    />
                                    <label
                                        className="form-check-label col-9 col-form-label text-nowrap"
                                        htmlFor={option}
                                    >
                                        {option}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div> */}
                    <div className="container">
                        <div className="row">
                            {options.map((option, index) => {
                                const id = `option-${index}`;

                                return (
                                    <div key={id} className="col-6 mb-3">
                                        <div className="form-check d-flex align-items-center">
                                            <input
                                                id={id}
                                                className="form-check-input me-2"
                                                type="checkbox"
                                                checked={safeReasons.includes(option)}
                                                onChange={() => handleChange(option)}
                                                disabled={isSubmitted}
                                            />
                                            <label className="form-check-label" htmlFor={id}>
                                                {option}
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>
            </div>
        </>
    )
}
